import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { makeApiCall } from '../../config/api';

// Special "All Trucks" option
const ALL_TRUCKS_OPTION = { id: 'all', name: 'All Trucks' };

const TruckProfitMarginReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(ALL_TRUCKS_OPTION); // Default to "All Trucks"
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [pricingConfig, setPricingConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Fetch trucks list
  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        const response = await makeApiCall('api/routes/trucks');
        if (response.ok) {
          const result = await response.json();
          setTrucks(result.trucks || []);
        }
      } catch (err) {
        console.error('Error fetching trucks:', err);
      }
    };
    fetchTrucks();

    // Default to last 30 days
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    setFromDate(from);
    setToDate(to);
  }, []);
  
  // Build options list with "All Trucks" at the top
  const truckOptions = [ALL_TRUCKS_OPTION, ...trucks];

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString().split('T')[0]);
      if (toDate) params.append('to_date', toDate.toISOString().split('T')[0]);
      
      // Only add truck_ids if a specific truck is selected (not "All Trucks")
      if (selectedTruck && selectedTruck.id !== 'all') {
        params.append('truck_ids', selectedTruck.id);
      }

      const response = await makeApiCall(`api/reports/truck-profit-margin?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data || []);
          setSummary(result.summary || []);
          setPricingConfig(result.pricing_config || {});
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching truck profit margin report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const truckIds = (selectedTruck && selectedTruck.id !== 'all') 
        ? selectedTruck.id 
        : null;

      const response = await makeApiCall('api/reports/truck-profit-margin/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString().split('T')[0] : null,
          to_date: toDate ? toDate.toISOString().split('T')[0] : null,
          truck_ids: truckIds,
          summary_data: summary
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `truck-profit-margin-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, []);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value ? new Date(e.target.value) : null);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value ? new Date(e.target.value) : null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  // Calculate totals
  const totals = summary.reduce((acc, truck) => {
    acc.revenue += truck.total_revenue || 0;
    acc.costs += truck.total_costs || 0;
    acc.profit += truck.total_profit || 0;
    acc.jobs += truck.total_jobs || 0;
    acc.km += truck.total_km || 0;
    acc.hours += truck.total_hours || 0;
    return acc;
  }, { revenue: 0, costs: 0, profit: 0, jobs: 0, km: 0, hours: 0 });

  const overallMargin = totals.revenue > 0 ? (totals.profit / totals.revenue) * 100 : 0;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              options={truckOptions}
              getOptionLabel={(option) => option.id === 'all' ? t('reports.allTrucks', 'All Trucks') : option.name}
              value={selectedTruck}
              onChange={(event, newValue) => setSelectedTruck(newValue || ALL_TRUCKS_OPTION)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disableClearable
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.liveLogs.truck')} 
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.id === 'all' ? (
                      <Typography fontWeight="bold">{t('reports.allTrucks', 'All Trucks')}</Typography>
                    ) : (
                      <>
                        <LocalShippingIcon fontSize="small" color="action" />
                        <Typography>{option.name}</Typography>
                      </>
                    )}
                  </Box>
                </li>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#1976d2' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || summary.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
              >
                PDF
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: '#1976d2', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {formatCurrency(totals.revenue)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <LocalShippingIcon sx={{ fontSize: 32, color: '#f57c00', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                  {formatCurrency(totals.costs)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Costs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: totals.profit >= 0 ? '#e8f5e9' : '#ffebee', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {totals.profit >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 32, color: '#4caf50', mb: 1 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 32, color: '#f44336', mb: 1 }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: totals.profit >= 0 ? '#4caf50' : '#f44336' }}>
                  {formatCurrency(totals.profit)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Profit
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: overallMargin >= 20 ? '#e8f5e9' : overallMargin >= 0 ? '#fff8e1' : '#ffebee', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: overallMargin >= 20 ? '#4caf50' : overallMargin >= 0 ? '#ff9800' : '#f44336' }}>
                  {overallMargin.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Profit Margin
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                  {totals.jobs}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Jobs Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ bgcolor: '#e0f7fa', height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#00838f' }}>
                  {Math.round(totals.km)} km
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Distance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Pricing Config Info */}
      {pricingConfig.default_daily_cost && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Cost Assumptions:</strong> Daily truck cost: {formatCurrency(pricingConfig.default_daily_cost)} | 
            Fuel: ${pricingConfig.fuel_price_per_liter}/L | 
            Efficiency: {pricingConfig.default_fuel_efficiency} L/100km
          </Typography>
        </Alert>
      )}

      {/* Report Data */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : summary.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocalShippingIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select a date range and click Load to view truck profit margins.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Truck</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Crew</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Days</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Jobs</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Revenue</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Costs</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Profit</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Margin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.map((truck) => {
                const crew = [truck.driver_name, truck.passenger_name].filter(Boolean).join(', ') || 'Unassigned';
                const isPositive = truck.total_profit >= 0;
                const marginColor = truck.profit_margin >= 20 ? '#4caf50' : truck.profit_margin >= 0 ? '#ff9800' : '#f44336';
                
                return (
                  <TableRow key={truck.truck_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon color="primary" fontSize="small" />
                        <Typography fontWeight="medium">{truck.truck_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{crew}</TableCell>
                    <TableCell align="right">{truck.total_days}</TableCell>
                    <TableCell align="right">{truck.total_jobs}</TableCell>
                    <TableCell align="right">{formatCurrency(truck.total_revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(truck.total_costs)}</TableCell>
                    <TableCell align="right" sx={{ color: isPositive ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                      {formatCurrency(truck.total_profit)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${truck.profit_margin}%`}
                        size="small"
                        sx={{ 
                          bgcolor: `${marginColor}20`, 
                          color: marginColor,
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Totals Row */}
              <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                <TableCell colSpan={2}>
                  <Typography fontWeight="bold">TOTAL</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{summary.reduce((sum, t) => sum + t.total_days, 0)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{totals.jobs}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(totals.revenue)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatCurrency(totals.costs)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ color: totals.profit >= 0 ? '#4caf50' : '#f44336' }}>
                  <Typography fontWeight="bold">{formatCurrency(totals.profit)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip 
                    label={`${overallMargin.toFixed(1)}%`}
                    size="small"
                    sx={{ 
                      bgcolor: overallMargin >= 20 ? '#e8f5e9' : overallMargin >= 0 ? '#fff8e1' : '#ffebee',
                      color: overallMargin >= 20 ? '#4caf50' : overallMargin >= 0 ? '#ff9800' : '#f44336',
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TruckProfitMarginReport;

